import { Directive, Input, OnInit, OnDestroy, ElementRef, Renderer2 } from '@angular/core';
import tippy, { Instance, Props, Placement } from 'tippy.js';

import { BrowserService } from '@cjm/shared/core';

/**
 * TippyDirective
 *
 * Directive to add tippy.js tooltips to an element.
 * This directive is standalone and can be used by adding the `tippy` attribute to an element.
 */
@Directive({
	selector: '[tippy]',
	standalone: true
})
export class TippyDirective implements OnInit, OnDestroy {
	/**
	 * The content to display within the tooltip.
	 */
	@Input('tippy') content: string = '';

	/**
	 * The placement of the tooltip relative to the element.
	 * Default value is 'bottom'.
	 */
	@Input('tippyPlacement') placement: Placement = 'bottom';

	private tippyInstance: Instance<Props> = null;
	private escListener: () => void;

	/**
	 * Constructor to inject dependencies.
	 * @param el - Reference to the host element.
	 * @param renderer
	 * @param browserService - Service to check if the code is running in the browser.
	 */
	constructor(
		private readonly el: ElementRef<HTMLElement>,
		private readonly renderer: Renderer2,
		private readonly browserService: BrowserService
	) {}

	/**
	 * Lifecycle hook that is called after data-bound properties are initialized.
	 * Initializes the tippy.js instance if the code is running in the browser.
	 */
	public ngOnInit(): void {
		if (!this.browserService.isBrowser) {
			return;
		}

		this.setUpInstance();
	}

	/**
	 * Lifecycle hook that is called when the directive is destroyed.
	 * Destroys the tippy.js instance to clean up resources.
	 */
	public ngOnDestroy(): void {
		this.tippyInstance.destroy();
	}

	/**
	 * setUpInstance
	 *
	 * Sets up the tippy.js instance with the provided configuration.
	 * Initializes the tooltip with the specified content, placement, and theme.
	 * The tooltip is interactive and allows HTML content.
	 *
	 * When the tooltip is hidden, it refreshes the instance.
	 * This is necessary to ensure that provided web-components are rendered again.
	 */
	private setUpInstance(): void {
		this.tippyInstance = tippy(this.el.nativeElement, {
			content: this.content,
			placement: this.placement,
			theme: 'vlo',
			interactive: true,
			allowHTML: true,
			trigger: 'mouseenter focus click',
			onShown: () => {
				// Denis: Set the aria-expanded attribute to true when the tooltip is shown.
				// tippy.js should set this automatically, but it seems to conflict with Angular
				// which leaves it at false.
				// https://atomiks.github.io/tippyjs/v6/accessibility/#interactivity
				this.el.nativeElement.setAttribute('aria-expanded', 'true');

				// Denis: Add an event listener to hide the tooltip when the escape key is pressed.
				this.escListener = this.renderer.listen('window', 'keydown.esc', () => {
					this.hideOnEsc();
				});
			},
			onHidden: () => {
				// Denis: Set the aria-expanded attribute to true when the tooltip is shown.
				// tippy.js should set this automatically, but it seems to conflict with Angular
				// which leaves it at false.
				// https://atomiks.github.io/tippyjs/v6/accessibility/#interactivity
				this.el.nativeElement.setAttribute('aria-expanded', 'false');

				// Denis: Remove the event listener when the tooltip is hidden.
				if (typeof this.escListener === 'function') {
					this.escListener();
				}

				// Denis: Refreshing the instance assures that potential custom elements are rendered again.
				this.refreshInstance();
			}
		});
	}

	/**
	 * refreshInstance
	 *
	 * Refreshes the tippy.js instance by destroying the current instance
	 * and setting up a new one.
	 */
	private refreshInstance(): void {
		if (this.tippyInstance) {
			this.tippyInstance.destroy();
		}

		this.setUpInstance();
	}

	/**
	 * hideOnEsc
	 *
	 * The hideOnEsc method is called when the user presses the escape key.
	 * It will hide the instance of the tooltip. Matching a11y requirements.
	 *
	 * Based on: https://atomiks.github.io/tippyjs/v6/plugins/#hideonesc
	 *
	 * @private
	 */
	private hideOnEsc(): void {
		this.tippyInstance.hide();
	}
}
